import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { computeTextDiff, type DiffTables } from "@/feedback/diff/text";
import type { CsvDiffTables } from "@/feedback/diff/csv";
import type { DiffFormat, DiffStyle } from "@/types/FeedbackResult";

/**
 * Renders the diff between generated and expected output as an HTML table.
 * Supports text and CSV formats. Both diff tables are computed once and cached;
 * switching between split and unified views swaps the rendered HTML.
 *
 * @element d-feedback-diff
 *
 * @prop {string} generated  - The student's actual output.
 * @prop {string} expected   - The expected output.
 * @prop {string} format     - "text" (default) or "csv".
 * @prop {string} diff-style - Active view: "split" or "unified".
 */
@customElement("d-feedback-diff")
export class FeedbackDiff extends DodonaElement {
    @property({ type: String }) generated: string = "";
    @property({ type: String }) expected: string = "";
    @property({ type: String }) format: DiffFormat = "text";
    @property({ type: String }) diffStyle: DiffStyle = "split";

    @state() private _tables: DiffTables | CsvDiffTables | null = null;
    private _csvGeneration = 0;

    override willUpdate(changedProperties: Map<string, unknown>): void {
        const inputsChanged =
            changedProperties.has("generated") || changedProperties.has("expected") || changedProperties.has("format");
        if (!inputsChanged) return;

        this._csvGeneration++;
        const gen = this._csvGeneration;

        if (this.format !== "csv") {
            this._tables = computeTextDiff(this.generated, this.expected);
            return;
        }

        // CSV path: papaparse is dynamic-imported the first time a CSV diff
        // appears. While the module loads, render an empty placeholder.
        this._tables = null;
        void import("@/feedback/diff/csv").then((mod) => {
            if (this._csvGeneration !== gen) return;
            const csvResult = mod.computeCsvDiffOrNull(this.generated, this.expected);
            this._tables = csvResult ?? computeTextDiff(this.generated, this.expected);
        });
    }

    render(): TemplateResult {
        const tables = this._tables;
        if (!tables) return html``;
        const activeHtml = this.diffStyle === "split" ? tables.split : tables.unified;
        return html`
            <div class="diffs">
                ${unsafeHTML(activeHtml)}
                ${"cutoffWarning" in tables && tables.cutoffWarning ? unsafeHTML(tables.cutoffWarning) : ""}
            </div>
        `;
    }
}
